/**
 * Application Entry Point
 * 
 * This file sets up the core providers and routing for the application.
 * Route definitions are in src/routes/index.tsx
 */

import React, { useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter } from "react-router-dom";

// Contexts
import { AuthProvider } from "@/contexts/AuthContext";
import { UserProvider } from "@/contexts/UserContext";

// Error Boundary
import ErrorBoundary from "@/components/ErrorBoundary";

// Routes
import { AppRoutes } from "@/routes";

// Providers
import { AndroidBackProvider } from "@/components/providers/AndroidBackProvider";

// Utils
import { applyViewportFixIfNeeded } from "@/utils/viewportFix";

/**
 * React Query Configuration
 * 
 * IMPORTANT: These settings ensure proper behavior with Cloudflare + Supabase:
 * - staleTime: 0 (data is immediately stale, will refetch on mount)
 * - gcTime: 5 minutes (keep in memory for quick back-navigation)
 * - refetchOnWindowFocus: true (refresh data when user returns)
 * - retry: 1 (single retry to handle transient network issues)
 * 
 * Individual queries can override these defaults with longer staleTime
 * for data that changes infrequently (e.g., reference data).
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 0, // Data is stale immediately - always check for updates
      gcTime: 5 * 60 * 1000, // 5 minutes garbage collection time
      refetchOnWindowFocus: true, // Refresh when user returns to tab
      retry: 1, // Single retry for transient failures
      refetchOnMount: true, // Always refetch when component mounts
    },
    mutations: {
      retry: 0, // No automatic retry for mutations
    },
  },
});

function App() {
  // Global viewport fix for mobile Chrome after OAuth redirects
  // Some browsers switch to "Desktop site" mode after visiting external domains
  useEffect(() => {
    // Check on initial load
    applyViewportFixIfNeeded();
    
    // Also check when page becomes visible (user returns from OAuth flow)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Small delay to let browser settle after tab switch
        setTimeout(applyViewportFixIfNeeded, 100);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary>
        {/* AuthProvider MUST be the outermost auth context — singleton initialization */}
        <AuthProvider>
          <UserProvider>
            <BrowserRouter>
              <AndroidBackProvider>
                <AppRoutes />
                <Toaster />
                <Sonner />
              </AndroidBackProvider>
            </BrowserRouter>
          </UserProvider>
        </AuthProvider>
      </ErrorBoundary>
    </QueryClientProvider>
  );
}

export default App;
